import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI, subscriptionAPI, historyAPI } from '@/utils/auth';
import { User, Mail, Phone, Coins, Calendar, LogOut, ArrowLeft, Crown, Globe, Download, Trash2, FileBox, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenInfo, setTokenInfo] = useState({ tokens: 0, is_admin: false });
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const { t, i18n } = useTranslation();

  // Detect current language from URL and update i18n
  useEffect(() => {
    const lang = location.pathname.includes('/en') ? 'en' : 'ru';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    loadTokenInfo();
    loadHistory();
  }, []);

  const loadTokenInfo = async () => {
    try {
      // Используем subscriptionAPI вместо authAPI
      const info = await subscriptionAPI.getTokens();
      setTokenInfo(info);
    } catch (error) {
      console.error('Error loading token info:', error);
      toast.error(t('profile.userDataError'));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const historyData = await historyAPI.getHistory();
      setHistory(historyData);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error(t('profile.historyLoadError'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDownload = async (itemId, fileName) => {
    try {
      const response = await historyAPI.downloadFile(itemId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('profile.downloadSuccess'));
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        toast.error(t('profile.fileNotFound'));
      } else {
        toast.error(t('profile.downloadError'));
      }
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm(t('profile.deleteConfirm'))) {
      return;
    }
    try {
      await historyAPI.deleteItem(itemId);
      setHistory(history.filter(item => item.id !== itemId));
      toast.success(t('profile.deleteSuccess'));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('profile.deleteError'));
    }
  };

  const handleLogout = () => {
    logout();
    toast.success(t('profile.logoutSuccess'));
    navigate(i18n.language === 'ru' ? '/' : '/en');
  };

  const handleLanguageSwitch = () => {
    const newLang = i18n.language === 'ru' ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
    if (newLang === 'ru') {
      navigate('/profile');
    } else {
      navigate('/profile/en');
    }
  };

  const formatDate = (dateString) => {
    const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  const backToHomePath = i18n.language === 'ru' ? '/' : '/en';

  if (!user) {
    return (
      <div className="min-h-screen paper-texture flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4">{t('profile.pleaseLogin')}</p>
          <Link to={i18n.language === 'ru' ? "/login" : "/login/en"} className="engraved-button engraved-button-red">
            {t('profile.login')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper-texture py-8 px-4" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <Link to={backToHomePath} className="inline-flex items-center gap-2 hover:text-[#C41E3A] transition-colors">
            <ArrowLeft size={20} />
            {t('profile.backToHome')}
          </Link>
          
          {/* Language Switcher */}
          <button
            onClick={handleLanguageSwitch}
            className="flex items-center gap-2 text-lg hover:text-[#C41E3A] transition-colors"
            aria-label={i18n.language === 'ru' ? t('language.switchTo') : t('language.switchTo')}
          >
            <Globe size={20} />
            <span className="hidden sm:inline">
              {i18n.language === 'ru' ? 'EN' : 'RU'}
            </span>
            <span className="sm:hidden">
              {i18n.language === 'ru' ? t('language.en') : t('language.ru')}
            </span>
          </button>
        </div>

        <div className="vintage-border bg-[#F5F1E8] p-8 engraving-texture">
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-4 border-4 border-[#C41E3A] rounded-full bg-[#E8E3D5] flex items-center justify-center">
              <User size={48} className="text-[#C41E3A]" />
            </div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('profile.title')}
            </h1>
            {tokenInfo.is_admin && (
              <div className="inline-flex flex-col items-center gap-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 border border-yellow-400 rounded-full">
                  <Crown size={16} className="text-yellow-600" />
                  <span className="text-yellow-800 font-semibold">{t('profile.admin')}</span>
                </div>
                <Link
                  to="/admin"
                  className="engraved-button engraved-button-red text-sm"
                >
                  Панель администратора
                </Link>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Информация о пользователе */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold border-b-2 border-black pb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('profile.basicInfo')}
              </h2>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 border-2 border-black bg-white">
                  <Mail className="text-[#C41E3A]" size={20} />
                  <div>
                    <p className="text-sm text-gray-600">{t('profile.email')}</p>
                    <p className="font-semibold">{user.email}</p>
                  </div>
                </div>

                {user.phone && (
                  <div className="flex items-center gap-3 p-3 border-2 border-black bg-white">
                    <Phone className="text-[#C41E3A]" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">{t('profile.phone')}</p>
                      <p className="font-semibold">{user.phone}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 p-3 border-2 border-black bg-white">
                  <Calendar className="text-[#C41E3A]" size={20} />
                  <div>
                    <p className="text-sm text-gray-600">{t('profile.registrationDate')}</p>
                    <p className="font-semibold">{formatDate(user.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Информация о токенах */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold border-b-2 border-black pb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('profile.tokenBalance')}
              </h2>

              <div className="text-center p-6 border-2 border-black bg-white">
                <Coins size={48} className="mx-auto mb-4 text-[#C41E3A]" />
                <p className="text-sm text-gray-600 mb-2">{t('profile.availableTokens')}</p>
                {loading ? (
                  <div className="flex justify-center">
                    <div className="w-8 h-8 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <>
                    <p className="text-4xl font-bold text-[#C41E3A]">
                      {tokenInfo.is_admin ? '∞' : tokenInfo.tokens}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {tokenInfo.is_admin ? t('profile.unlimitedAccess') : t('profile.tokensForSegmentation')}
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-3">
                <Link
                  to="/klkt-segment"
                  className="w-full engraved-button engraved-button-red text-center block"
                >
                  {t('profile.startSegmentation')}
                </Link>

                <Link
                  to={i18n.language === 'ru' ? "/subscription" : "/subscription/en"}
                  className="w-full engraved-button text-center block"
                >
                  {t('profile.buyTokens')}
                </Link>
              </div>
            </div>
          </div>

          {/* История сканов */}
          <div className="border-t-2 border-black pt-6 mt-6">
            <h2 className="text-2xl font-bold border-b-2 border-black pb-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('profile.scanHistory')}
            </h2>

            {historyLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileBox size={48} className="mx-auto mb-4 opacity-50" />
                <p>{t('profile.noScanHistory')}</p>
                <Link
                  to="/klkt-segment"
                  className="text-[#C41E3A] hover:underline mt-2 inline-block"
                >
                  {t('profile.startSegmentationLink')}
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 border-2 border-black bg-white hover:border-[#C41E3A] transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 border-2 border-[#C41E3A] rounded-lg bg-[#F5F1E8] flex items-center justify-center flex-shrink-0">
                        <FileBox className="text-[#C41E3A]" size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{item.file_name}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatDate(item.created_at)}
                          </span>
                          {item.file_size && (
                            <span>{(item.file_size / 1024 / 1024).toFixed(2)} MB</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownload(item.id, item.file_name)}
                        className="p-2 hover:bg-[#F5F1E8] rounded-lg transition-colors"
                        title={t('profile.download')}
                      >
                        <Download size={20} className="text-[#C41E3A]" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        title={t('profile.delete')}
                      >
                        <Trash2 size={20} className="text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Действия */}
          <div className="border-t-2 border-black pt-6 mt-6">
            <button
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 w-full engraved-button hover:bg-red-600 hover:text-white transition-colors"
            >
              <LogOut size={20} />
              {t('profile.logout')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;